import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { CompanyPerDiemRatesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { countryName } from 'modules/core/lib/countryList'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

const CompanyPerDiemRatesList = () => {
  const {
    access: { locale },
  } = useCurrentSession()

  const dataSource = useGraphqlDataCollectionSource({
    document: CompanyPerDiemRatesDataCollectionDocument,
    mapData: (data) => data.expenses.officialPerDiemRatesConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      search,
      sort: sortings?.[0] ? `${sortings[0].field}:${sortings[0].order}` : undefined,
    }),
    paginationType: 'infinite-scroll',
    perPage: 25,
    mapPagination: (data) => ({
      total: data.expenses.officialPerDiemRatesConnection.totalCount ?? 0,
      cursor: data.expenses.officialPerDiemRatesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.officialPerDiemRatesConnection.pageInfo.hasNextPage ?? false,
    }),
    search: {
      enabled: true,
    },
    sortings: {
      destinationCountry: {
        label: i18n.t(
          'expenses_v2.settings.rate_configurations.datatable.fields.destination_country'
        ),
      },
    },
    defaultSortings: {
      field: 'destinationCountry',
      order: 'asc',
    },
  })

  return (
    <Box paddingX="s12" style={{ height: '100%', overflow: 'auto' }}>
      <OneDataCollection
        id="company-per-diem-rates/v1"
        storage={{
          features: ['sortings', 'search'],
        }}
        fullHeight
        emptyStates={{
          'no-data': {
            title: i18n.t('common.no_data_available'),
            description: '',
          },
        }}
        source={dataSource}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              allowColumnReordering: true,
              columns: [
                {
                  id: 'destinationCountry',
                  label: i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.fields.country'
                  ),
                  render: (item) => ({
                    type: 'text' as const,
                    value: countryName(item.destinationCountry, locale),
                  }),
                  sorting: 'destinationCountry',
                },
                {
                  id: 'destination',
                  label: i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.fields.destination_country'
                  ),
                  render: (item) => {
                    const value = item.locality ? `${item.locality}` : ``

                    return {
                      type: 'text' as const,
                      value,
                    }
                  },
                },
                {
                  id: 'amountFullCents',
                  label: i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.fields.amount_full'
                  ),
                  render: (item) => ({
                    type: 'text' as const,
                    value: amountWithCurrency(Number(item.amountFullCents), item.currency, locale),
                  }),
                },
                {
                  id: 'amountPartialCents',
                  label: i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.fields.amount_partial'
                  ),
                  render: (item) => ({
                    type: 'text' as const,
                    value: amountWithCurrency(
                      Number(item.amountPartialCents),
                      item.currency,
                      locale
                    ),
                  }),
                },
                {
                  id: 'amountOvernightCents',
                  label: i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.fields.amount_overnight'
                  ),
                  render: (item) => ({
                    type: 'text' as const,
                    value: amountWithCurrency(
                      Number(item.amountOvernightCents),
                      item.currency,
                      locale
                    ),
                  }),
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}

export default CompanyPerDiemRatesList
